//Node Modules
import React, { useEffect, useRef, useCallback, useMemo } from "react";

//Pages

//BinaryForge Components

//3rd Party Components
import { Chart, registerables } from "chart.js";
import "chartjs-adapter-luxon";
import zoomPlugin from "chartjs-plugin-zoom";

//Services

//Helpers
import { historialLineChartConfig } from "../../config/chart";
import { getDatasetValueRange, getLineColour } from "../../helpers/chart";

//Other

const LineChartDate = ({ data, timestamps, measureName, deviceMap = {} }) => {
	const chart = useRef(null);
	const tooltipRef = useRef(null);
	Chart.register(...registerables, zoomPlugin);

	const normalisedData = useMemo(() => {
		if (!data || typeof data !== "object" || Array.isArray(data)) {
			return {};
		}

		return Object.entries(data).reduce((acc, [seriesName, points]) => {
			if (!Array.isArray(points)) {
				return acc;
			}

			const seriesPoints = points
				.map((point) => {
					const value = Number(point?.measure_value);
					if (!Number.isFinite(value)) {
						return null;
					}

					const timestampValue = point?.timestamp ? new Date(point.timestamp) : null;
					if (!(timestampValue instanceof Date) || Number.isNaN(timestampValue.getTime())) {
						return null;
					}

					return {
						...point,
						timestamp: timestampValue,
						measure_value: value,
					};
				})
				.filter(Boolean);

			if (seriesPoints.length > 0) {
				acc[seriesName] = seriesPoints;
			}

			return acc;
		}, {});
	}, [data]);

	const valueRange = useMemo(() => getDatasetValueRange(normalisedData), [normalisedData]);

	const datasets = useMemo(() => {
		return Object.entries(normalisedData).map(([seriesName, points], idx) => {
			const colour = getLineColour(idx);
			const isDenseSeries = points.length > 300;
			const deviceInfo = deviceMap[seriesName];

			return {
				label: deviceInfo?.name ?? seriesName,
				deviceId: seriesName,
				deviceBreadcrumb: deviceInfo?.zoneBreadcrumb ?? null,
				data: points,
				//No shaded area. One device under a filled curve reads well, a dozen overlapping
				//ones do not, and RSSI reports are usually the dozen.
				fill: false,
				borderColor: colour.borderColour,
				backgroundColor: colour.backgroundColour,
				//Kept on the dataset so hover dimming can swap between them without recalculating
				//a colour on every mouse move.
				baseBorderColour: colour.borderColour,
				dimBorderColour: colour.dimColour,
				tension: 0.25,
				radius: isDenseSeries ? 0 : 4,
				pointRadius: isDenseSeries ? 0 : 5,
				pointHoverRadius: isDenseSeries ? 3 : 8,
				hitRadius: 10,
			};
		});
	}, [normalisedData, deviceMap]);

	const getUpdatedOptions = useCallback(() => {
		let options = historialLineChartConfig(measureName, timestamps, valueRange ?? undefined);
		options.animation = false;
		options.normalized = true;
		//Nearest without intersect, because a dense series draws no points to land on - exact hit
		//testing makes those lines all but unhoverable.
		options.interaction = { mode: "nearest", intersect: false };
		//Fade every other line while one is hovered, so a single device's trend can be followed
		//through a crowded chart. Only redraw when a colour actually changed, or every mouse move
		//over the canvas would trigger an update.
		options.onHover = (event, activeElements, chart) => {
			const hoveredIndex = activeElements?.[0]?.datasetIndex;
			let changed = false;

			chart.data.datasets.forEach((dataset, idx) => {
				const colour =
					hoveredIndex === undefined || idx === hoveredIndex
						? dataset.baseBorderColour
						: dataset.dimBorderColour;

				if (colour && dataset.borderColor !== colour) {
					dataset.borderColor = colour;
					changed = true;
				}
			});

			if (changed) chart.update("none");
		};
		options.plugins = {
			...options.plugins,
			decimation: {
				enabled: true,
				algorithm: "lttb",
				samples: 200,
			},
			legend: {
				...options.plugins?.legend,
				onHover: (event, legendItem, legend) => {
					if (!tooltipRef.current || !event.native) return;
					const dataset = legend.chart.data.datasets[legendItem.datasetIndex];
					if (!dataset?.deviceId) return;
					const lines = [dataset.deviceId, dataset.deviceBreadcrumb].filter(Boolean);
					//textContent, not innerHTML - zone labels are user-entered
					tooltipRef.current.textContent = lines.join("\n");
					tooltipRef.current.style.display = "block";
					tooltipRef.current.style.left = `${event.native.clientX + 12}px`;
					tooltipRef.current.style.top = `${event.native.clientY + 12}px`;
				},
				onLeave: () => {
					if (tooltipRef.current) tooltipRef.current.style.display = "none";
				},
			},
		};
		return options;
	}, [measureName, timestamps, valueRange]);

	const createChart = useCallback(() => {
		if (!chart.current || datasets.length === 0) {
			return null;
		}

		const ctx = chart.current.getContext("2d");

		return new Chart(ctx, {
			type: "line",
			data: {
				datasets,
			},
			options: getUpdatedOptions(),
		});
	}, [datasets, getUpdatedOptions]);

	useEffect(() => {
		// process.env.NODE_ENV === "development" && console.log("Graph Data:", data);
		let lineChart;
		if (datasets.length > 0) {
			lineChart = createChart();
		}

		return () => {
			if (lineChart) lineChart.destroy();
		};
	}, [createChart, datasets.length]);

	return (
		<div className="chartContainer">
			<canvas id="myChart" ref={chart}></canvas>
			<div ref={tooltipRef} className="chartLegendTooltip" />
		</div>
	);
};

export default LineChartDate;
